import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { transactionAPI } from '../api/client';
import { formatAmount, formatShortAmount } from '../utils/currency';
import { Loader2, TrendingDown, TrendingUp, Calendar, ChevronDown, ChevronLeft, ChevronRight, Filter, X } from 'lucide-react';
import { useTokens } from '../context/ThemeContext';
import { CATEGORIES } from '../constants/categories';

// ── Time period helpers ──────────────────────────────────────────────────────

const VIEW_MODE_LABELS = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
  custom: 'Custom',
};
const VIEW_MODES = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly', 'custom'];

const now = new Date();

const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

function getQuarterRange(year, quarterIndex) {
  const startMonth = quarterIndex * 3;
  const start = new Date(year, startMonth, 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(year, startMonth + 3, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function getQuarterLabel(year, quarterIndex) {
  return `Q${quarterIndex + 1} ${year}`;
}

function getModeDates(mode, { year, month, weekStart, dailyDate, quarterYear, quarterIndex }) {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  switch (mode) {
    case 'yearly': {
      const start = new Date(year, 0, 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(year, 11, 31, 23, 59, 59, 999);
      return { start, end: year === now.getFullYear() ? today : end };
    }
    case 'quarterly':
      return getQuarterRange(quarterYear, quarterIndex);
    case 'monthly': {
      const start = new Date(year, month, 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
      return { start, end };
    }
    case 'weekly': {
      const end = new Date(weekStart);
      end.setDate(end.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return { start: weekStart, end };
    }
    case 'daily': {
      const start = new Date(dailyDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(dailyDate);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    default:
      return null;
  }
}

// ── Mini bar/line chart — pure SVG, no deps ──────────────────────────────────

function SpendingChart({ data, baseCurrency }) {
  const t = useTokens();
  if (!data || data.length === 0) return <p style={{ fontSize: '13px', color: t.textMuted, margin: 0 }}>No data for this period.</p>;

  const W = 600, H = 200, PAD = { top: 20, right: 20, bottom: 40, left: 60 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const maxVal = Math.max(...data.map(d => d.total), 1);
  const yTicks = 4;
  const yStep = maxVal / yTicks;

  // Map data points to SVG coordinates
  const points = data.map((d, i) => ({
    x: PAD.left + (i / Math.max(data.length - 1, 1)) * innerW,
    y: PAD.top + innerH - (d.total / maxVal) * innerH,
    label: d.label,
    total: d.total,
  }));

  // Smooth cubic bezier path
  const linePath = points.reduce((path, pt, i) => {
    if (i === 0) return `M ${pt.x},${pt.y}`;
    const prev = points[i - 1];
    const cpX = (prev.x + pt.x) / 2;
    return `${path} C ${cpX},${prev.y} ${cpX},${pt.y} ${pt.x},${pt.y}`;
  }, '');

  // Closed area path for gradient fill
  const areaPath = points.length > 0
    ? `${linePath} L ${points[points.length - 1].x},${PAD.top + innerH} L ${points[0].x},${PAD.top + innerH} Z`
    : '';

  const gradId = 'spending-grad';

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366f1" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#6366f1" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Y-axis grid + labels */}
      {Array.from({ length: yTicks + 1 }, (_, i) => {
        const val = yStep * i;
        const y = PAD.top + innerH - (val / maxVal) * innerH;
        return (
          <g key={i}>
            <line x1={PAD.left} x2={PAD.left + innerW} y1={y} y2={y} stroke={t.borderLight} strokeWidth="1" strokeDasharray={i === 0 ? 'none' : '4 3'} />
            <text x={PAD.left - 8} y={y + 4} textAnchor="end" fontSize="10" fill={t.textMuted}>
              {formatShortAmount(val, baseCurrency)}
            </text>
          </g>
        );
      })}

      {/* Gradient fill under line */}
      {areaPath && <path d={areaPath} fill={`url(#${gradId})`} />}

      {/* Line */}
      <path d={linePath} fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

      {/* Dots + x-labels */}
      {points.map((pt, i) => (
        <g key={i}>
          <circle cx={pt.x} cy={pt.y} r="3.5" fill="#6366f1" stroke={t.surface} strokeWidth="2" />
          <text x={pt.x} y={H - PAD.bottom + 16} textAnchor="middle" fontSize="10" fill={t.textSecondary}>
            {pt.label}
          </text>
        </g>
      ))}

      {/* X axis baseline */}
      <line x1={PAD.left} x2={PAD.left + innerW} y1={PAD.top + innerH} y2={PAD.top + innerH} stroke={t.border} strokeWidth="1" />
    </svg>
  );
}

// ── Category donut chart ─────────────────────────────────────────────────────

const PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#6b7280',
];

function DonutChart({ data, baseCurrency }) {
  const t = useTokens();
  if (!data || data.length === 0) return <p style={{ fontSize: '13px', color: t.textMuted, margin: 0 }}>No category data for this period.</p>;

  const total = data.reduce((s, d) => s + d.total, 0);
  const R = 70, CX = 90, CY = 90, stroke = 28;
  const circumference = 2 * Math.PI * R;

  let offset = 0;
  const slices = data.slice(0, 10).map((d, i) => {
    const pct = d.total / total;
    const dash = pct * circumference;
    const slice = { ...d, pct, dashArray: `${dash} ${circumference - dash}`, offset, color: PALETTE[i % PALETTE.length] };
    offset += dash;
    return slice;
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
      <svg width="180" height="180" style={{ flexShrink: 0 }}>
        {slices.map((s, i) => (
          <circle
            key={i}
            cx={CX} cy={CY} r={R}
            fill="none"
            stroke={s.color}
            strokeWidth={stroke}
            strokeDasharray={s.dashArray}
            strokeDashoffset={-s.offset}
            style={{ transform: 'rotate(-90deg)', transformOrigin: `${CX}px ${CY}px` }}
          />
        ))}
        <text x={CX} y={CY - 6} textAnchor="middle" fontSize="11" fill={t.textSecondary}>Total</text>
        <text x={CX} y={CY + 10} textAnchor="middle" fontSize="12" fontWeight="600" fill={t.text}>
          {formatShortAmount(total, baseCurrency)}
        </text>
      </svg>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: '160px' }}>
        {slices.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: s.color, flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: t.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {s.category}
            </span>
            <span style={{ fontSize: '12px', color: t.textSecondary, fontWeight: '500' }}>
              {(s.pct * 100).toFixed(1)}%
            </span>
            <span style={{ fontSize: '12px', color: t.text, fontWeight: '600', fontFamily: 'monospace' }}>
              {formatShortAmount(s.total, baseCurrency)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main AnalyticsView ────────────────────────────────────────────────────────

function AnalyticsView({ baseCurrency = 'INR', excludedCategories = [] }) {
  const t = useTokens();
  const navigate = useNavigate();
  const styles = makeStyles(t);
  const [viewMode, setViewMode] = useState('monthly');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [dailyDate, setDailyDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [quarterYear, setQuarterYear] = useState(now.getFullYear());
  const [quarterIndex, setQuarterIndex] = useState(Math.floor(now.getMonth() / 3));
  // Custom
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    return toLocalDateStr(d);
  });
  const [customEnd, setCustomEnd] = useState(() => toLocalDateStr(now));

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const toDateStr = toLocalDateStr;

  const today = new Date(); today.setHours(0, 0, 0, 0);

  const canGoNext = () => {
    if (viewMode === 'daily') return dailyDate < today;
    if (viewMode === 'weekly') {
      const nextWeek = new Date(weekStart); nextWeek.setDate(nextWeek.getDate() + 7);
      return nextWeek <= today;
    }
    if (viewMode === 'monthly') {
      return selectedYear < now.getFullYear() || selectedMonth < now.getMonth();
    }
    if (viewMode === 'quarterly') {
      return quarterYear < now.getFullYear() || quarterIndex < Math.floor(now.getMonth() / 3);
    }
    if (viewMode === 'yearly') return selectedYear < now.getFullYear();
    return false;
  };

  const navigatePeriod = (dir) => {
    if (viewMode === 'daily') {
      setDailyDate(prev => { const d = new Date(prev); d.setDate(d.getDate() + dir); return d; });
    } else if (viewMode === 'weekly') {
      setWeekStart(prev => { const d = new Date(prev); d.setDate(d.getDate() + dir * 7); return d; });
    } else if (viewMode === 'monthly') {
      let m = selectedMonth + dir;
      let y = selectedYear;
      if (m < 0) { m = 11; y -= 1; }
      if (m > 11) { m = 0; y += 1; }
      setSelectedMonth(m);
      setSelectedYear(y);
    } else if (viewMode === 'quarterly') {
      let q = quarterIndex + dir;
      let y = quarterYear;
      if (q < 0) { q = 3; y -= 1; }
      if (q > 3) { q = 0; y += 1; }
      setQuarterIndex(q);
      setQuarterYear(y);
    } else if (viewMode === 'yearly') {
      setSelectedYear(prev => prev + dir);
    }
  };

  const getPeriodLabel = () => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    if (viewMode === 'yearly') return String(selectedYear);
    if (viewMode === 'quarterly') return getQuarterLabel(quarterYear, quarterIndex);
    if (viewMode === 'monthly') return `${monthNames[selectedMonth]} ${selectedYear !== now.getFullYear() ? selectedYear : ''}`.trim();
    if (viewMode === 'weekly') {
      const end = new Date(weekStart); end.setDate(end.getDate() + 6);
      const opts = { month: 'short', day: 'numeric' };
      return `${weekStart.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', opts)}`;
    }
    if (viewMode === 'daily') {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      return dailyDate.getTime() === today.getTime() ? 'Today' : dailyDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
    if (viewMode === 'custom' && customStart && customEnd) return `${customStart} – ${customEnd}`;
    return '';
  };


  const excludedKey = excludedCategories.join(',');
  const categoryKey = categoryFilter.join(',');
  const weekStartStr = toDateStr(weekStart);
  const dailyDateStr = toDateStr(dailyDate);

  useEffect(() => {
    if (viewMode === 'custom' && (!customStart || !customEnd)) return;

    const groupBy = viewMode === 'daily' ? 'hour' : viewMode === 'weekly' ? 'day' : 'month';
    let start, end;
    if (viewMode === 'custom') {
      start = customStart;
      end = customEnd;
    } else {
      const dates = getModeDates(viewMode, {
        year: selectedYear, month: selectedMonth,
        weekStart: new Date(weekStartStr),
        dailyDate: new Date(dailyDateStr),
        quarterYear, quarterIndex,
      });
      if (!dates) return;
      start = toDateStr(dates.start);
      end = toDateStr(dates.end);
    }

    let cancelled = false;
    setLoading(true);
    transactionAPI.getSummary({
      startDate: start, endDate: end, groupBy,
      excludedCategories: excludedKey,
      ...(categoryKey ? { categories: categoryKey } : {}),
    })
      .then(res => { if (!cancelled) setSummary(res.data); })
      .catch(err => console.error('Analytics fetch error:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [viewMode, selectedYear, selectedMonth, weekStartStr, dailyDateStr, quarterYear, quarterIndex, customStart, customEnd, excludedKey, categoryKey]);

  // ── Unpack summary from backend ─────────────────────────────────────────────

  const transactions = summary?.transactions || [];
  const totalSpend = summary?.totalSpend ?? 0;
  const totalIncome = summary?.totalIncome ?? 0;
  const netSavings = summary?.netSavings ?? 0;
  const categoryData = summary?.categoryBreakdown || [];
  const topMerchants = (summary?.topMerchants || []).slice(0, 8);
  const debits = transactions.filter(tx => tx.transactionType !== 'credit');
  const credits = transactions.filter(tx => tx.transactionType === 'credit');

  // Chart series — fill in zero slots for periods with no transactions
  const chartData = useMemo(() => {
    if (!summary) return [];
    const serverBuckets = Object.fromEntries((summary.chartSeries || []).map(b => [b.key, b]));
    const bucket = {};
    const today = new Date();

    if (viewMode === 'yearly') {
      const maxMonth = selectedYear === now.getFullYear() ? now.getMonth() : 11;
      for (let m = 0; m <= maxMonth; m++) {
        const key = `${selectedYear}-${String(m + 1).padStart(2, '0')}`;
        const label = new Date(selectedYear, m, 1).toLocaleDateString('en-US', { month: 'short' });
        bucket[key] = { label, total: serverBuckets[key]?.total ?? 0 };
      }
      return Object.values(bucket);
    }

    if (viewMode === 'quarterly') {
      const startMonth = quarterIndex * 3;
      for (let m = startMonth; m < startMonth + 3; m++) {
        const slotDate = new Date(quarterYear, m, 1);
        if (slotDate > today) break;
        const key = `${quarterYear}-${String(m + 1).padStart(2, '0')}`;
        const label = slotDate.toLocaleDateString('en-US', { month: 'short' });
        bucket[key] = { label, total: serverBuckets[key]?.total ?? 0 };
      }
      return Object.values(bucket);
    }

    if (viewMode === 'monthly') {
      const monthStart = new Date(selectedYear, selectedMonth, 1);
      const monthEnd = new Date(selectedYear, selectedMonth + 1, 0);
      today.setHours(23, 59, 59, 999);
      const firstDay = monthStart.getDay();
      const daysToMon = firstDay === 0 ? 6 : firstDay - 1;
      const cursor = new Date(monthStart);
      cursor.setDate(cursor.getDate() - daysToMon);
      cursor.setHours(0, 0, 0, 0);
      while (cursor <= monthEnd && cursor <= today) {
        const key = cursor.toISOString().slice(0, 10);
        const label = cursor.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        bucket[key] = { label, total: serverBuckets[key]?.total ?? 0 };
        cursor.setDate(cursor.getDate() + 7);
      }
      return Object.values(bucket);
    }

    if (viewMode === 'weekly') {
      today.setHours(0, 0, 0, 0);
      for (let i = 0; i < 7; i++) {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        if (d > today) break;
        const key = d.toISOString().slice(0, 10);
        const label = d.toLocaleDateString('en-US', { weekday: 'short' });
        bucket[key] = { label, total: serverBuckets[key]?.total ?? 0 };
      }
      return Object.values(bucket);
    }

    if (viewMode === 'daily') {
      const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
      const isToday = dailyDate.getTime() === todayMidnight.getTime();
      const maxHour = isToday ? new Date().getHours() : 23;
      for (let h = 0; h <= maxHour; h++) {
        const key = String(h).padStart(2, '0');
        const label = h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`;
        bucket[key] = { label, total: serverBuckets[key]?.total ?? 0 };
      }
      return Object.values(bucket);
    }

    if (viewMode === 'custom' && customStart && customEnd) {
      const start = new Date(customStart);
      const end = new Date(customEnd);
      const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
      while (cursor <= end) {
        const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
        const label = cursor.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        bucket[key] = { label, total: serverBuckets[key]?.total ?? 0 };
        cursor.setMonth(cursor.getMonth() + 1);
      }
      return Object.values(bucket);
    }

    return [];
  }, [summary, viewMode, selectedYear, selectedMonth, selectedYear, quarterYear, quarterIndex, weekStart, dailyDate, customStart, customEnd]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Analytics</h1>
        </div>

        {/* Dropdown + filter */}
        <div style={styles.controls}>
          {/* Category filter */}
          <div style={styles.filterWrap}>
            <div style={styles.filterPill}>
              <button
                onClick={() => setShowFilterDropdown(v => !v)}
                style={{ ...styles.filterButton, ...(categoryFilter.length > 0 ? styles.filterButtonActive : {}) }}
                title="Filter by category"
              >
                <Filter size={14} />
                {categoryFilter.length > 0 && <span style={styles.filterCount}>{categoryFilter.length}</span>}
              </button>
              {categoryFilter.length > 0 && (
                <button onClick={() => setCategoryFilter([])} style={styles.filterClearBtn} title="Clear filters">
                  <X size={12} />
                </button>
              )}
            </div>
            {showFilterDropdown && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setShowFilterDropdown(false)} />
                <div style={styles.filterDropdown}>
                  {(() => {
                    const allCats = CATEGORIES;
                    const allSelected = allCats.every(c => categoryFilter.includes(c));
                    return (
                      <>
                        <div
                          role="button"
                          tabIndex={0}
                          style={{ ...styles.filterOption, ...(allSelected ? styles.filterOptionActive : {}) }}
                          onClick={() => setCategoryFilter(allSelected ? [] : [...allCats])}
                          onKeyDown={e => e.key === 'Enter' && setCategoryFilter(allSelected ? [] : [...allCats])}
                        >
                          <span style={{ ...styles.filterCheckbox, ...(allSelected ? styles.filterCheckboxActive : {}) }}>
                            {allSelected ? '✓' : ''}
                          </span>
                          Select all
                        </div>
                        <div style={styles.filterDivider} />
                        {allCats.map(cat => {
                          const selected = categoryFilter.includes(cat);
                          return (
                            <div
                              key={cat}
                              role="button"
                              tabIndex={0}
                              style={{ ...styles.filterOption, ...(selected ? styles.filterOptionActive : {}) }}
                              onClick={() => setCategoryFilter(prev => selected ? prev.filter(c => c !== cat) : [...prev, cat])}
                              onKeyDown={e => e.key === 'Enter' && setCategoryFilter(prev => selected ? prev.filter(c => c !== cat) : [...prev, cat])}
                            >
                              <span style={{ ...styles.filterCheckbox, ...(selected ? styles.filterCheckboxActive : {}) }}>
                                {selected ? '✓' : ''}
                              </span>
                              {cat}
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              </>
            )}
          </div>

          {/* View mode dropdown */}
          <div data-tour="analytics-view-mode" style={styles.dropdownContainer}>
            <button
              style={styles.dropdownButton}
              onClick={() => setDropdownOpen(o => !o)}
            >
              {VIEW_MODE_LABELS[viewMode]}
              <ChevronDown size={14} style={{ marginLeft: '4px', transition: 'transform 0.2s', transform: dropdownOpen ? 'rotate(180deg)' : 'none' }} />
            </button>
            {dropdownOpen && (
              <div style={styles.dropdownMenu}>
                {VIEW_MODES.map(m => (
                  <button
                    key={m}
                    style={{ ...styles.dropdownItem, ...(viewMode === m ? styles.dropdownItemActive : {}) }}
                    onClick={() => { setViewMode(m); setDropdownOpen(false); }}
                  >
                    {VIEW_MODE_LABELS[m]}
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Period navigation */}
      {viewMode !== 'custom' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
          <button onClick={() => navigatePeriod(-1)} style={{ ...styles.dropdownButton, padding: '6px 10px' }}>
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: '14px', fontWeight: '500', color: t.textSlate, minWidth: '140px', textAlign: 'center' }}>
            {getPeriodLabel()}
          </span>
          <button onClick={() => navigatePeriod(1)} disabled={!canGoNext()} style={{ ...styles.dropdownButton, padding: '6px 10px', opacity: canGoNext() ? 1 : 0.3, cursor: canGoNext() ? 'pointer' : 'default' }}>
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Custom date range */}
      {viewMode === 'custom' && (
        <div style={styles.customRange}>
          <Calendar size={14} color="#6b7280" />
          <input
            type="date"
            value={customStart}
            onChange={e => setCustomStart(e.target.value)}
            style={styles.dateInput}
          />
          <span style={styles.dateSep}>to</span>
          <input
            type="date"
            value={customEnd}
            onChange={e => setCustomEnd(e.target.value)}
            style={styles.dateInput}
          />
        </div>
      )}

      {loading && (
        <div style={styles.loader}>
          <Loader2 size={36} style={{ animation: 'spin 1s linear infinite' }} />
          <p>Loading analytics…</p>
        </div>
      )}

      {!loading && (
        <>
          {/* Summary cards */}
          <div data-tour="analytics-cards" style={styles.cards}>
            <SummaryCard
              title="Total Spend"
              value={formatAmount(totalSpend, baseCurrency)}
              icon={<TrendingDown size={18} color="#ef4444" />}
              accent="#ef4444"
              valueColor="#ef4444"
            />
            <SummaryCard
              title="Total Income"
              value={formatAmount(totalIncome, baseCurrency)}
              icon={<TrendingUp size={18} color="#10b981" />}
              accent="#10b981"
              valueColor="#10b981"
            />
            <SummaryCard
              title="Net Savings"
              value={formatAmount(Math.abs(netSavings), baseCurrency)}
              sub={netSavings >= 0 ? 'surplus' : 'deficit'}
              icon={netSavings >= 0
                ? <TrendingUp size={18} color="#3b82f6" />
                : <TrendingDown size={18} color="#f59e0b" />}
              accent={netSavings >= 0 ? '#3b82f6' : '#f59e0b'}
              valueColor={netSavings >= 0 ? '#3b82f6' : '#f59e0b'}
            />
            <SummaryCard
              title="Transactions"
              value={transactions.length}
              sub={`${debits.length} debits · ${credits.length} credits`}
              accent={t.border}
              valueColor={t.text}
            />
          </div>

          {/* Spending over time */}
          <div data-tour="analytics-chart" style={styles.card}>
            <h2 style={styles.cardTitle}>Spending Over Time</h2>
            <SpendingChart data={chartData} baseCurrency={baseCurrency} />
          </div>

          {/* Category breakdown */}
          <div data-tour="analytics-categories" style={styles.card}>
            <h2 style={styles.cardTitle}>Spend by Category</h2>
            <DonutChart data={categoryData} baseCurrency={baseCurrency} />
          </div>

          {/* Top merchants */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Top Merchants</h2>
            {topMerchants.length > 0 ? (
              <div style={styles.merchantList}>
                {topMerchants.map((m, i) => {
                  const pct = (m.total / totalSpend) * 100;
                  return (
                    <div
                      key={i}
                      style={{ ...styles.merchantRow, cursor: 'pointer' }}
                      onClick={() => navigate('/transactions', { state: { searchQuery: m.name } })}
                      title={`View transactions for ${m.name}`}
                    >
                      <div style={styles.merchantLeft}>
                        <span style={styles.merchantRank}>{i + 1}</span>
                        <span style={styles.merchantName}>{m.name}</span>
                      </div>
                      <div style={styles.merchantRight}>
                        <div style={styles.merchantBarWrap}>
                          <div style={{ ...styles.merchantBar, width: `${pct}%` }} />
                        </div>
                        <span style={styles.merchantAmount}>{formatAmount(m.total, baseCurrency)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ fontSize: '13px', color: t.textMuted, margin: 0 }}>No merchants in this period.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ title, value, sub, icon, accent, valueColor }) {
  const t = useTokens();
  return (
    <div style={{ borderRadius: '12px', padding: '16px', backgroundColor: t.surface, border: `2px solid ${accent}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '12px', fontWeight: '500', color: t.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</span>
        {icon}
      </div>
      <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'monospace', marginBottom: '2px', color: valueColor }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', color: t.textMuted }}>{sub}</div>}
    </div>
  );
}

function makeStyles(t) { return {
  container: {
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '4px',
  },
  title: {
    margin: '0 0 4px 0',
    fontSize: '24px',
    fontWeight: '700',
    color: t.text,
  },
  subtitle: {
    margin: 0,
    fontSize: '13px',
    color: t.textMuted,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  dropdownContainer: {
    position: 'relative',
  },
  dropdownButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    border: `1px solid ${t.border}`,
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    backgroundColor: t.surface,
    color: t.textSlate,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    backgroundColor: t.surface,
    border: `1px solid ${t.border}`,
    borderRadius: '10px',
    boxShadow: t.dropdownShadow,
    zIndex: 100,
    minWidth: '130px',
    overflow: 'hidden',
  },
  dropdownItem: {
    display: 'block',
    width: '100%',
    padding: '10px 20px',
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '13px',
    color: t.textSlate,
    cursor: 'pointer',
    textAlign: 'left',
  },
  dropdownItemActive: {
    backgroundColor: t.chipBg,
    fontWeight: '600',
  },
  filterWrap: {
    position: 'relative',
  },
  filterPill: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${t.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: t.surface,
  },
  filterButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    height: '32px',
    padding: '0 10px',
    border: 'none',
    backgroundColor: 'transparent',
    color: t.textSecondary,
    cursor: 'pointer',
  },
  filterButtonActive: {
    backgroundColor: t.itemSelected,
    color: t.itemSelectedText,
  },
  filterCount: {
    fontSize: '11px',
    fontWeight: '700',
    lineHeight: 1,
  },
  filterClearBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '32px',
    width: '28px',
    border: 'none',
    borderLeft: `1px solid ${t.border}`,
    backgroundColor: t.itemSelected,
    color: t.itemSelectedText,
    cursor: 'pointer',
    padding: 0,
  },
  filterDropdown: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    backgroundColor: t.surface,
    border: `1px solid ${t.border}`,
    borderRadius: '10px',
    boxShadow: t.dropdownShadow,
    zIndex: 200,
    minWidth: '180px',
    maxHeight: '260px',
    overflowY: 'auto',
    padding: '4px 0',
  },
  filterDivider: {
    height: '1px',
    backgroundColor: t.border,
    margin: '4px 0',
  },
  filterOption: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '7px 14px',
    backgroundColor: 'transparent',
    fontSize: '13px',
    color: t.textSlate,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  },
  filterOptionActive: {
    color: t.text,
    fontWeight: '500',
  },
  filterCheckbox: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    fontSize: '10px',
    border: `1.5px solid ${t.inputBorder}`,
    borderRadius: '4px',
    flexShrink: 0,
    color: 'transparent',
    backgroundColor: t.inputBg,
  },
  filterCheckboxActive: {
    backgroundColor: '#2563eb',
    borderColor: '#2563eb',
    color: 'white',
  },
  navArrows: {
    display: 'flex',
    gap: '2px',
  },
  navBtn: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${t.border}`,
    borderRadius: '8px',
    backgroundColor: t.surface,
    color: t.textSlate,
    cursor: 'pointer',
    fontSize: '18px',
    lineHeight: 1,
  },
  navBtnDisabled: {
    opacity: 0.35,
    cursor: 'default',
  },
  customRange: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: t.surface,
    borderRadius: '10px',
    boxShadow: t.cardShadow,
  },
  dateSep: {
    fontSize: '13px',
    color: t.textMuted,
  },
  dateInput: {
    border: `1px solid ${t.inputBorder}`,
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '13px',
    color: t.text,
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: t.inputBg,
  },
  loader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    color: t.textSecondary,
    gap: '16px',
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: t.surface,
    borderRadius: '12px',
    boxShadow: t.cardShadow,
  },
  emptyTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: t.text,
    margin: '0 0 8px 0',
  },
  emptyText: {
    fontSize: '13px',
    color: t.textMuted,
    margin: 0,
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  summaryCard: {
    borderRadius: '12px',
    padding: '16px',
  },
  summaryCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  summaryCardTitle: {
    fontSize: '12px',
    fontWeight: '500',
    color: t.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  summaryCardValue: {
    fontSize: '20px',
    fontWeight: '700',
    fontFamily: 'monospace',
    marginBottom: '2px',
  },
  summaryCardSub: {
    fontSize: '11px',
    color: t.textMuted,
  },
  card: {
    backgroundColor: t.surface,
    borderRadius: '12px',
    boxShadow: t.cardShadow,
    padding: '20px',
  },
  cardTitle: {
    margin: '0 0 16px 0',
    fontSize: '15px',
    fontWeight: '600',
    color: t.text,
  },
  merchantList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  merchantRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  merchantLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '180px',
    flexShrink: 0,
  },
  merchantRank: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: t.chipBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: '600',
    color: t.textSecondary,
    flexShrink: 0,
  },
  merchantName: {
    fontSize: '13px',
    color: t.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  merchantRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1,
  },
  merchantBarWrap: {
    flex: 1,
    height: '6px',
    backgroundColor: t.chipBg,
    borderRadius: '3px',
    overflow: 'hidden',
  },
  merchantBar: {
    height: '100%',
    backgroundColor: '#3b82f6',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  merchantAmount: {
    fontSize: '12px',
    fontWeight: '600',
    color: t.text,
    fontFamily: 'monospace',
    whiteSpace: 'nowrap',
    minWidth: '80px',
    textAlign: 'right',
  },
}; }

export default AnalyticsView;
